import React, {useState,useEffect,useContext,useRef} from 'react'

import {Result,Modal,Select,Spin,Form,Layout,Tabs,Button,Input,Row,Col,Divider,Alert,Checkbox,Table,Space,AutoComplete,Radio} from "antd";

import useFetch from "use-http";
import {
    ControlOutlined,
    StepForwardOutlined,
    ReloadOutlined,
    PlusOutlined,
    DeleteOutlined,
    FieldTimeOutlined,
    LockOutlined,
    UnlockOutlined,
    WifiOutlined
} from '@ant-design/icons'

import {successN,errorN} from "../../general";
import {Context} from "../../store"
import superadminPasscode from "../../config/superadminPasscode";

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { Content } = Layout;

// Port of the Windows frmAdmin_SystemSettings screen: Date & Time, Notice, Email and Running No.
// Every group is one settings record saved through /api/settings/{id}; each Update button saves only its own record.

const TIME_RX = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
const HHMM_RX = /^([01]?\d|2[0-3]):[0-5]\d$/;
const EMAIL_RX = /^[^\s@;,]+@[^\s@;,]+\.[^\s@;,]+$/;

const DEFAULT_FOOTER = 'Thank you for your business and please come again!';

// Windows cboEarlyCheckin (1 - 14) and cboLateCheckout (1 - 8), stored as text
const EARLY_CHECKIN = Array.from({length: 14}, (v, i) => (i + 1).toString());
const LATE_CHECKOUT = Array.from({length: 8}, (v, i) => (i + 1).toString());

// Windows cboSS / cboSE: index 0 = 0, 1 = 1, 2 = -1
const PERIODS = [{value: 0, label: 'Current Day'}, {value: 1, label: 'Day After'}, {value: -1, label: 'Day Before'}];

// Windows MyGlobal.gvNewspaperList (duplicates removed)
const NEWSPAPERS = Array.from(new Set(["The Borneo Post", "Business Times", "Daily Express", "The Edge", "Focus Malaysia", "Malay Mail", "The Malaysian Reserve", "Malaysian Today", "New Sabah Times", "New Sarawak Tribune", "New Straits Times", "The Star", "The Sun", "Berita Harian", "Harakah", "Harian Metro", "Sinar Harian", "Utusan Borneo", "Utusan Sarawak", "China Press", "Guang Ming", "Kwong Wah Yit Poh", "Morning Post", "Nanyang Siang Pau", "Oriental Daily News", "Overseas Chinese Daily News", "See Hua Daily News", "Sin Chew Daily", "United Daily News", "Makkal Osai", "Malaysia Nanban", "Tamil Nesan"]));

// Windows cboChannelName and cboFeeCalc
const CHANNEL_NAMES = ['', 'STAAH', 'Wubook'];
const FEE_CALC = ['fixed', 'percentage'];

const TIMEZONE_RX = /^-?\d{1,2}:[0-5]\d(:[0-5]\d)?$/;
const DECIMAL_RX = /^-?\d+(\.\d+)?$/;

// name = form field, path = location inside record.data, kind = how the value is shown/validated/stored
// list fields are replaced as a whole (as the Windows app does), so they are only saved when nobody else changed them meanwhile
const GROUPS = {
    time: {
        title: 'Date & Time',
        cacheKey: 'timesetting',
        fields: [
            {name: 'start', label: 'Check In', path: ['start'], kind: 'time'},
            {name: 'end', label: 'Check Out', path: ['end'], kind: 'time'},
            {name: 'buffer', label: 'Check Out Buffer', path: ['buffer'], kind: 'hhmm'},
            {name: 'cutOut', label: 'Cut Out Time', path: ['cutOut'], kind: 'time'},
            {name: 'housekeeping', label: 'Housekeeping', path: ['housekeeping'], kind: 'hhmm'},
            {name: 'earlyCheckin', label: 'Early Check In', path: ['earlyCheckin'], kind: 'text'},
            {name: 'lateCheckout', label: 'Late Check Out', path: ['lateCheckout'], kind: 'text'},
            {name: 'shiftStartTime', label: 'First Shift Begin', path: ['shift', 'shiftStart', 'time'], kind: 'time', default: '00:00:00'},
            {name: 'shiftStartPeriod', label: 'First Shift Begin Day', path: ['shift', 'shiftStart', 'period'], kind: 'period', default: 0},
            {name: 'shiftEndTime', label: 'Last Shift End', path: ['shift', 'shiftEnd', 'time'], kind: 'time', default: '23:59:59'},
            {name: 'shiftEndPeriod', label: 'Last Shift End Day', path: ['shift', 'shiftEnd', 'period'], kind: 'period', default: 0},
        ]
    },
    notice: {
        title: 'Notice',
        cacheKey: 'noticesetting',
        fields: [
            {name: 'folio', label: 'Folio Notice', path: ['folio'], kind: 'notice'},
            {name: 'invoice', label: 'Invoice Notice', path: ['invoice'], kind: 'notice'},
            {name: 'receipt', label: 'Payment Slip Notice', path: ['receipt'], kind: 'notice'},
            {name: 'footer', label: 'Folio & Payment Slip Footer', path: ['footer'], kind: 'notice', default: DEFAULT_FOOTER},
        ]
    },
    email: {
        title: 'Email',
        cacheKey: 'emailsetting',
        fields: [
            {name: 'notification', label: 'Notification', path: ['notification'], kind: 'email'},
            {name: 'notificationCC', label: 'Notification CC', path: ['notificationCC'], kind: 'email'},
            {name: 'complaint', label: 'Complaint', path: ['complaint'], kind: 'email'},
            {name: 'complaintCC', label: 'Complaint CC', path: ['complaintCC'], kind: 'email'},
            {name: 'reservation', label: 'Reservation', path: ['reservation'], kind: 'email'},
            {name: 'enquiry', label: 'Enquiry', path: ['enquiry'], kind: 'email'},
        ]
    },
    systemparameter: {
        title: 'System Parameter',
        cacheKey: 'systemparametersetting',
        fields: [
            {name: 'currency', label: 'Currency', path: ['currency'], kind: 'currency'},
            {name: 'timezone', label: 'Time Zone', path: ['timezone'], kind: 'timezone'},
            {name: 'defaultDeposit', label: 'Deposit', path: ['defaultDeposit'], kind: 'decimal'},
            {name: 'skipRounding', label: 'Skip Rounding For Booking & Folio', path: ['skipRounding'], kind: 'bool'},
            {name: 'roundingAmount', label: 'Rounding', path: ['roundingAmount'], kind: 'decimal', default: '0.05', blank: 0.05},
        ]
    },
    fee: {
        title: 'Fees',
        cacheKey: 'feesetting',
        fields: [
            {name: 'fees', label: 'Fees', path: ['fees'], kind: 'list'},
        ]
    },
    guestParameter: {
        title: 'Guest Parameter',
        cacheKey: 'guestparametersetting',
        fields: [
            {name: 'newspaper', label: 'Newspapers', path: ['newspaper'], kind: 'strings'},
        ]
    },
    channel: {
        title: 'Channel Manager & OTA',
        cacheKey: 'channelsetting',
        fields: [
            {name: 'channelName', label: 'Channel Manager', path: ['channelName'], kind: 'trim'},
            {name: 'username', label: 'Channel Username', path: ['channelAccess', 'username'], kind: 'trim'},
            {name: 'password', label: 'Channel Password', path: ['channelAccess', 'password'], kind: 'trim', secret: true},
            {name: 'lcode', label: 'Property Code', path: ['channelAccess', 'lcode'], kind: 'trim'},
            {name: 'otaChannels', label: 'OTA Channels', path: ['otaChannels'], kind: 'list'},
            {name: 'otaRooms', label: 'Room Codes', path: ['otaRooms'], kind: 'list'},
        ]
    },
    // Windows frmAdmin_HardwareSetting. Door lock is used for card issue/read/remove, WiFi by Bookings/wifi.js.
    // data.services (Built-In / Services) is shown only: Windows lets just super admin change it, so the web never writes it.
    device: {
        title: 'Devices',
        cacheKey: 'devicesetting',
        fields: [
            {name: 'doorLock', label: 'Enable Door Lock', path: ['doorLock', 'doorLock'], kind: 'bool'},
            {name: 'lockType', label: 'Lock Type', path: ['doorLock', 'type'], kind: 'trim'},
            {name: 'lockCode', label: 'Lock Code', path: ['doorLock', 'code'], kind: 'trim'},
            {name: 'mykad', label: 'Enable MyKad Reader', path: ['mykad'], kind: 'bool'},
            {name: 'wifi', label: 'Enable WiFi Guest Accounts', path: ['wifi', 'wifi'], kind: 'bool'},
            {name: 'routerIP', label: 'Router IP', path: ['wifi', 'routerIP'], kind: 'trim'},
            {name: 'guestSSID', label: 'Guest WiFi Name (SSID)', path: ['wifi', 'guestSSID'], kind: 'trim'},
        ]
    },
    // Windows gpCustom, only shown in super admin mode
    custom: {
        title: 'Custom',
        cacheKey: 'customsetting',
        superAdmin: true,
        fields: [
            {name: 'availabilityManualOverride', label: 'Online Availability Manual Override', path: ['availabilityManualOverride'], kind: 'bool'},
            {name: 'stopSale', label: 'Stop Online Sales Control', path: ['stopSale'], kind: 'bool'},
            {name: 'rateControl', label: 'Online Rate Control', path: ['rateControl'], kind: 'bool'},
            {name: 'backDate', label: 'Allow Back Date', path: ['backDate'], kind: 'bool'},
            {name: 'hideInvoiceDate', label: 'Edit Folio', path: ['hideInvoiceDate'], kind: 'bool'},
            {name: 'fullPaymentBeforeInvoice', label: 'Full Payment Before Issue Invoice', path: ['fullPaymentBeforeInvoice'], kind: 'bool'},
            {name: 'allowEditAccessCardDateTime', label: 'Allow Edit Access Card DateTime', path: ['allowEditAccessCardDateTime'], kind: 'bool'},
            {name: 'allowUserCancelFolio', label: 'Allow Cashier Cancel Folio', path: ['allowUserCancelFolio'], kind: 'bool'},
            {name: 'deleteFolio', label: 'Allow Delete Folio', path: ['deleteFolio'], kind: 'bool'},
            {name: 'restrictDevice', label: 'Restrict Device', path: ['restrictDevice'], kind: 'bool'},
            {name: 'telnoMandatory', label: 'Not using-Tel. No. Mandatory', path: ['telnoMandatory'], kind: 'bool'},
            {name: 'blockDiscountCashier', label: 'Disallow Discount By Cashier', path: ['blockDiscountCashier'], kind: 'bool'},
            {name: 'miniFolio', label: 'Mini Folio', path: ['miniFolio'], kind: 'bool'},
            {name: 'miniReceipt', label: 'Mini Receipt', path: ['miniReceipt'], kind: 'bool'},
            {name: 'endFolio', label: 'Allow End Folio', path: ['endFolio'], kind: 'bool'},
            {name: 'walkinAnyRefund', label: 'Allow Any Walk-In Refund', path: ['walkinAnyRefund'], kind: 'bool'},
            {name: 'unitName', label: 'Custom Unit Name', path: ['unitName'], kind: 'unitname', default: 'Unit'},
        ]
    },
}

// Windows "Running No." group: one record per document type, same three fields each
const RUNNING_NO = [
    {type: 'folio', title: 'Folio'},
    {type: 'invoice', title: 'Invoice'},
    {type: 'receipt', title: 'Receipt'},
    {type: 'booking', title: 'Booking'},
];

for (let r of RUNNING_NO)
{
    GROUPS[r.type] = {
        title: `${r.title} Running No.`,
        cacheKey: null,
        fields: [
            {name: 'prefix', label: 'Prefix', path: ['prefix'], kind: 'trim'},
            {name: 'length', label: 'Length', path: ['length'], kind: 'int'},
            {name: 'lastNo', label: 'No.', path: ['lastNo'], kind: 'int'},
        ]
    };
}

// ---- Super Admin passcode (hash made with tools/superadmin-passcode-generator.html) ----

const PASSCODE_MAX_TRIES = 5;
const PASSCODE_LOCK_MS = 5 * 60 * 1000;
const PASSCODE_LOCK_KEY = 'superadminlock';
const CUSTOM_LOG_KEY = 'customsettinglog';
const CUSTOM_LOG_MAX = 200;

function passcodeConfigured()
{
    return superadminPasscode !== undefined && superadminPasscode !== null &&
        typeof superadminPasscode.salt === 'string' && superadminPasscode.salt !== '' &&
        typeof superadminPasscode.hash === 'string' && superadminPasscode.hash !== '' &&
        Number(superadminPasscode.iterations) >= 310000;
}

function fromBase64(s)
{
    const bin = atob(s);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        bytes[i] = bin.charCodeAt(i);
    return bytes;
}

// compares every byte so the time taken does not depend on where the first difference is
function sameBytes(a, b)
{
    let diff = a.length ^ b.length;
    for (let i = 0; i < Math.max(a.length, b.length); i++)
        diff |= (a[i] || 0) ^ (b[i] || 0);
    return diff === 0;
}

async function checkPasscode(passcode)
{
    const salt = fromBase64(superadminPasscode.salt);
    const expected = fromBase64(superadminPasscode.hash);
    const key = await window.crypto.subtle.importKey('raw', new TextEncoder().encode(passcode), 'PBKDF2', false, ['deriveBits']);
    const bits = await window.crypto.subtle.deriveBits({name: 'PBKDF2', hash: 'SHA-256', salt: salt, iterations: Number(superadminPasscode.iterations)}, key, expected.length * 8);
    return sameBytes(new Uint8Array(bits), expected);
}

// wrong-try counter; kept in this browser only, so it is a deterrent and not a hard limit
function readLock()
{
    try
    {
        const v = JSON.parse(localStorage.getItem(PASSCODE_LOCK_KEY));
        if (v !== null && typeof v === 'object')
            return {fails: Number(v.fails) || 0, until: Number(v.until) || 0};
    }
    catch (e)
    {
    }
    return {fails: 0, until: 0};
}

function writeLock(lock)
{
    try
    {
        if (lock === null)
            localStorage.removeItem(PASSCODE_LOCK_KEY);
        else
            localStorage.setItem(PASSCODE_LOCK_KEY, JSON.stringify(lock));
    }
    catch (e)
    {
    }
}

function readCustomLog()
{
    try
    {
        const v = JSON.parse(localStorage.getItem(CUSTOM_LOG_KEY));
        return Array.isArray(v) ? v : [];
    }
    catch (e)
    {
        return [];
    }
}

function addCustomLog(entries)
{
    try
    {
        const log = [...entries, ...readCustomLog()].slice(0, CUSTOM_LOG_MAX);
        localStorage.setItem(CUSTOM_LOG_KEY, JSON.stringify(log));
    }
    catch (e)
    {
    }
}

function getPath(obj, path)
{
    let o = obj;
    for (let p of path)
    {
        if (o === undefined || o === null)
            return undefined;
        o = o[p];
    }
    return o;
}

function setPath(obj, path, value)
{
    let o = obj;
    for (let i = 0; i < path.length - 1; i++)
    {
        if (o[path[i]] === undefined || o[path[i]] === null || typeof o[path[i]] !== 'object')
            o[path[i]] = {};
        o = o[path[i]];
    }
    o[path[path.length - 1]] = value;
}

// Stored value -> form value
function toForm(field, value)
{
    if (value === undefined || value === null)
        value = field.default;

    if (field.kind === 'list' || field.kind === 'strings')
        return Array.isArray(value) ? JSON.parse(JSON.stringify(value)).filter(x => x !== '') : [];

    if (field.kind === 'bool')
        return value === true || value === 'true' || value === 'True';

    if (field.kind === 'currency')
        return value !== undefined && value !== null && value.code !== undefined ? value.code : undefined;

    if (value === undefined || value === null)
        return field.kind === 'period' ? 0 : '';

    if (field.kind === 'decimal')
        return value.toString();

    if (field.kind === 'hhmm')
        return value.toString().substring(0, 5);

    if (field.kind === 'int')
        return value.toString();

    return value;
}

// Form value -> stored value, in the shape the Windows app writes
function toStored(field, value)
{
    if (field.kind === 'time')
    {
        let v = (value || '').trim();
        return v.length === 5 ? v + ':00' : v;
    }
    if (field.kind === 'hhmm')
    {
        let p = (value || '').trim().split(':');
        return p.length < 2 ? '' : p[0].padStart(2, '0') + ':' + p[1] + ':00';
    }
    if (field.kind === 'unitname')
    {
        // Windows: a blank unit name is saved as "Unit"
        let v = (value || '').trim();
        return v === '' ? 'Unit' : v;
    }
    if (field.kind === 'timezone')
    {
        let v = (value || '').trim();
        if (!TIMEZONE_RX.test(v))
            return v;
        let neg = v.startsWith('-');
        let p = v.replace('-', '').split(':');
        return (neg ? '-' : '') + p[0].padStart(2, '0') + ':' + p[1] + ':' + (p[2] !== undefined ? p[2] : '00');
    }
    if (field.kind === 'decimal')
    {
        let v = value === undefined || value === null ? '' : value.toString().trim();
        if (v === '')
            return field.blank !== undefined ? field.blank : '';
        return parseFloat(v);
    }
    if (field.kind === 'bool')
        return value === true;
    if (field.kind === 'list')
        return value;
    if (field.kind === 'strings')
        return (value || []).map(x => x.trim());
    if (field.kind === 'int')
        return value === undefined || value === null || value.toString().trim() === '' ? '' : parseInt(value, 10);
    if (field.kind === 'period')
        return parseInt(value, 10);
    if (field.kind === 'trim' || field.kind === 'email')
        return (value || '').trim();
    if (field.kind === 'notice')
        // the Windows app stores multi-line notices with \r\n; textareas give \n
        return value === undefined || value === null ? '' : value.toString().replace(/\r?\n/g, '\r\n');

    return value === undefined || value === null ? '' : value;
}

function sameValue(a, b)
{
    if (typeof a === 'object' || typeof b === 'object')
        return JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);

    let x = a === undefined || a === null ? '' : a.toString();
    let y = b === undefined || b === null ? '' : b.toString();
    return x === y;
}

// Windows "Fees" grid: Name / Type / Amount with Add and row delete
function FeeList({value, onChange, disabled}) {
    const [name, setName] = useState('');
    const [calc, setCalc] = useState(undefined);
    const [amount, setAmount] = useState('');
    const rows = value || [];

    const add = () => {
        if (name.trim() === '' || calc === undefined)
        {
            errorN('Please input fee Name and choose Type.');
            return;
        }
        if (!DECIMAL_RX.test(amount.trim()))
        {
            errorN('Amount is not valid. Enter numbers only.');
            return;
        }
        onChange([...rows, {name: name.trim(), calc: calc, amount: parseFloat(amount.trim())}]);
        setName('');
        setCalc(undefined);
        setAmount('');
    }

    return (
        <>
            <Space wrap style={{marginBottom: '10px'}}>
                <Input placeholder="Name" value={name} onChange={e => setName(e.target.value)} disabled={disabled} style={{width: '200px'}} />
                <Select placeholder="Type" value={calc} onChange={setCalc} disabled={disabled} style={{width: '120px'}}>
                    {FEE_CALC.map(v => <Option key={v} value={v}>{v}</Option>)}
                </Select>
                <Input placeholder="Amount" value={amount} onChange={e => setAmount(e.target.value)} disabled={disabled} style={{width: '100px'}} />
                <Button onClick={add} disabled={disabled}><PlusOutlined /> Add</Button>
            </Space>
            <Table size="small" pagination={false} dataSource={rows.map((r, i) => ({...r, key: i}))}
                columns={[
                    {title: 'Name', dataIndex: 'name'},
                    {title: 'Type', dataIndex: 'calc'},
                    {title: 'Amount', dataIndex: 'amount', align: 'right'},
                    {title: '', key: 'del', width: 50, render: (t, r, i) => <Button size="small" danger disabled={disabled} onClick={() => onChange(rows.filter((x, j) => j !== i))}><DeleteOutlined /></Button>},
                ]} />
        </>
    );
}

// Windows OTA grid: OTA Name (supplier) / Channel ID
function OtaChannelList({value, onChange, suppliers, disabled}) {
    const [supplier, setSupplier] = useState(undefined);
    const [code, setCode] = useState('');
    const rows = value || [];

    const add = () => {
        if (supplier === undefined)
        {
            errorN('Please choose OTA Name.');
            return;
        }
        if (!/^\d+$/.test(code.trim()))
        {
            errorN('Channel ID is not valid. Enter numbers only.');
            return;
        }
        let s = suppliers.find(x => x.id === supplier);
        onChange([...rows, {name: {id: s.id, name: s.name}, id: code.trim()}]);
        setSupplier(undefined);
        setCode('');
    }

    return (
        <>
            <Space wrap style={{marginBottom: '10px'}}>
                <Select placeholder="OTA Name" value={supplier} onChange={setSupplier} disabled={disabled} style={{width: '200px'}} showSearch optionFilterProp="children">
                    {suppliers.map(v => <Option key={v.id} value={v.id}>{v.name}</Option>)}
                </Select>
                <Input placeholder="Channel ID" value={code} onChange={e => setCode(e.target.value)} disabled={disabled} style={{width: '120px'}} />
                <Button onClick={add} disabled={disabled}><PlusOutlined /> Add</Button>
            </Space>
            <Table size="small" pagination={false} dataSource={rows.map((r, i) => ({...r, key: i}))}
                columns={[
                    {title: 'OTA Name', key: 'name', render: (t, r) => r.name !== null && r.name !== undefined ? r.name.name : ''},
                    {title: 'ID', key: 'sid', render: (t, r) => r.name !== null && r.name !== undefined ? r.name.id : ''},
                    {title: 'Channel ID', dataIndex: 'id'},
                    {title: '', key: 'del', width: 50, render: (t, r, i) => <Button size="small" danger disabled={disabled} onClick={() => onChange(rows.filter((x, j) => j !== i))}><DeleteOutlined /></Button>},
                ]} />
        </>
    );
}

// Windows room code grid: Resource Type / Code, one code per resource type
function RoomCodeList({value, onChange, roomTypes, disabled}) {
    const [roomType, setRoomType] = useState(undefined);
    const [code, setCode] = useState('');
    const rows = value || [];

    const add = () => {
        if (roomType === undefined)
        {
            errorN('Please choose Resource Type.');
            return;
        }
        if (rows.some(x => x.roomType !== null && x.roomType !== undefined && x.roomType.id === roomType))
        {
            errorN('Resource Type Code Exist.');
            return;
        }
        if (!/^\d+$/.test(code.trim()))
        {
            errorN('Code is not valid. Enter numbers only.');
            return;
        }
        let t = roomTypes.find(x => x.id === roomType);
        onChange([...rows, {roomType: {id: t.id, name: t.name}, roomCode: null, originalRoomCode: code.trim()}]);
        setRoomType(undefined);
        setCode('');
    }

    return (
        <>
            <Space wrap style={{marginBottom: '10px'}}>
                <Select placeholder="Resource Type" value={roomType} onChange={setRoomType} disabled={disabled} style={{width: '200px'}} showSearch optionFilterProp="children">
                    {roomTypes.map(v => <Option key={v.id} value={v.id}>{v.name}</Option>)}
                </Select>
                <Input placeholder="Code" value={code} onChange={e => setCode(e.target.value)} disabled={disabled} style={{width: '120px'}} />
                <Button onClick={add} disabled={disabled}><PlusOutlined /> Add</Button>
            </Space>
            <Table size="small" pagination={false} dataSource={rows.map((r, i) => ({...r, key: i}))}
                columns={[
                    {title: 'Resource', key: 'name', render: (t, r) => r.roomType !== null && r.roomType !== undefined ? r.roomType.name : ''},
                    {title: 'ID', key: 'rid', render: (t, r) => r.roomType !== null && r.roomType !== undefined ? r.roomType.id : ''},
                    {title: 'Room Code', dataIndex: 'originalRoomCode'},
                    {title: '', key: 'del', width: 50, render: (t, r, i) => <Button size="small" danger disabled={disabled} onClick={() => onChange(rows.filter((x, j) => j !== i))}><DeleteOutlined /></Button>},
                ]} />
        </>
    );
}

function SystemSettings(props) {
    const { get, put, response, loading } = useFetch()
    const [state,dispatch] = useContext(Context)
    const [records, setRecords] = useState({});
    const [loaded, setLoaded] = useState(false);
    const [activeTab, setActiveTab] = useState('time');
    const initial = useRef({});
    // one confirm/save at a time, so a double click cannot send two updates
    const busy = useRef(false);
    // super admin mode lives only in this page's memory: it is lost on reload or when leaving the page
    const [superAdmin, setSuperAdmin] = useState(false);
    const superAdminRef = useRef(false);
    const [passOpen, setPassOpen] = useState(false);
    const [passValue, setPassValue] = useState('');
    const [passError, setPassError] = useState('');
    const [passChecking, setPassChecking] = useState(false);
    const [logOpen, setLogOpen] = useState(false);
    const [logRows, setLogRows] = useState([]);

    const [timeForm] = Form.useForm()
    const [noticeForm] = Form.useForm()
    const [emailForm] = Form.useForm()
    const [folioForm] = Form.useForm()
    const [invoiceForm] = Form.useForm()
    const [receiptForm] = Form.useForm()
    const [bookingForm] = Form.useForm()
    const [sysForm] = Form.useForm()
    const [feeForm] = Form.useForm()
    const [guestForm] = Form.useForm()
    const [channelForm] = Form.useForm()
    const [customForm] = Form.useForm()
    const [deviceForm] = Form.useForm()

    const forms = {time: timeForm, notice: noticeForm, email: emailForm, folio: folioForm, invoice: invoiceForm, receipt: receiptForm, booking: bookingForm,
        systemparameter: sysForm, fee: feeForm, guestParameter: guestForm, channel: channelForm, device: deviceForm, custom: customForm};

    const [currencyList, setCurrencyList] = useState([]);
    const [supplierList, setSupplierList] = useState([]);
    const [roomTypeList, setRoomTypeList] = useState([]);

    const allowed = state.role === 'administrator' || state.role === 'manager';

    useEffect(() => {

        if (allowed)
        {
            loadSettings();
        }

        return () => {

        }
    }, [allowed])

    // the Custom tab (and its form) only exists while unlocked, so fill it when it appears
    useEffect(() => {
        superAdminRef.current = superAdmin;

        if (superAdmin && initial.current.custom !== undefined)
        {
            forms.custom.resetFields();
            forms.custom.setFieldsValue(initial.current.custom);
        }
    }, [superAdmin])

    // warn before the browser reloads or closes the page with unsaved edits
    useEffect(() => {
        const warn = (e) => {
            if (dirtyGroups().length > 0)
            {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', warn);

        return () => {
            window.removeEventListener('beforeunload', warn);
        }
    }, [])

    function fillForm(type, record)
    {
        let values = {};
        for (let f of GROUPS[type].fields)
        {
            values[f.name] = toForm(f, getPath(record !== null && record.data !== undefined && record.data !== null ? record.data : {}, f.path));
        }
        initial.current[type] = values;

        if (GROUPS[type].superAdmin === true && !superAdminRef.current)
            return;

        forms[type].resetFields();
        forms[type].setFieldsValue(values);
    }

    // compares in stored form, so "8:00" vs "08:00:00" or a trailing space is not a change
    function normalised(f, v)
    {
        if (f.kind === 'currency' || f.kind === 'list')
            return v === undefined ? null : v;
        return toStored(f, v);
    }

    function changedFields(type)
    {
        if (initial.current[type] === undefined || (GROUPS[type].superAdmin === true && !superAdminRef.current))
            return [];

        const values = forms[type].getFieldsValue(true);
        return GROUPS[type].fields.filter(f => !sameValue(normalised(f, values[f.name]), normalised(f, initial.current[type][f.name])));
    }

    function dirtyGroups()
    {
        return Object.keys(GROUPS).filter(t => changedFields(t).length > 0);
    }

    function onReload()
    {
        let dirty = dirtyGroups();

        if (dirty.length === 0)
        {
            loadSettings();
            return;
        }

        Modal.confirm({
            title: 'Discard unsaved changes?',
            content: `Unsaved changes in: ${dirty.map(t => GROUPS[t].title).join(', ')}`,
            okText: 'Discard & Reload',
            cancelText: 'Cancel',
            onOk: () => loadSettings()
        });
    }

    // Super admin is limited to administrators. The role is checked with the server (/api/me), not taken from
    // the browser's cached role, and it is checked again before every Custom save.
    async function verifySuperAdmin()
    {
        try
        {
            let me = await get(`/api/me`)
            return response.ok && me !== null && me !== undefined && me.active !== false && me.auth === 'administrator';
        }
        catch (err)
        {
            return false;
        }
    }

    function onSuperAdmin()
    {
        if (superAdmin)
        {
            if (changedFields('custom').length > 0)
                forms.custom.setFieldsValue(initial.current.custom);
            if (activeTab === 'custom')
                setActiveTab('time');
            setSuperAdmin(false);
            return;
        }

        if (!passcodeConfigured())
        {
            errorN('Super Admin passcode not set up. Please contact system administrator.');
            return;
        }

        setPassValue('');
        setPassError('');
        setPassOpen(true);
    }

    function closePasscode()
    {
        setPassValue('');
        setPassError('');
        setPassOpen(false);
    }

    // passcode first, then the server must still say this user is an active administrator
    async function submitPasscode()
    {
        if (passChecking)
            return;

        let lock = readLock();
        if (lock.until > Date.now())
        {
            setPassError(`Too many wrong tries. Try again in ${Math.ceil((lock.until - Date.now()) / 60000)} minute(s).`);
            return;
        }
        if (lock.until !== 0)
            lock = {fails: 0, until: 0};

        if (passValue === '')
        {
            setPassError('Please enter the passcode.');
            return;
        }

        if (!window.crypto || !window.crypto.subtle)
        {
            setPassError('This page must be opened over HTTPS to check the passcode.');
            return;
        }

        setPassChecking(true);
        try
        {
            let ok = false;
            try
            {
                ok = await checkPasscode(passValue);
            }
            catch (err)
            {
                ok = false;
            }

            setPassValue('');

            if (!ok)
            {
                let fails = lock.fails + 1;
                if (fails >= PASSCODE_MAX_TRIES)
                {
                    writeLock({fails: 0, until: Date.now() + PASSCODE_LOCK_MS});
                    setPassError(`Wrong passcode. Too many wrong tries: locked for ${PASSCODE_LOCK_MS / 60000} minutes.`);
                }
                else
                {
                    writeLock({fails: fails, until: 0});
                    setPassError(`Wrong passcode. ${PASSCODE_MAX_TRIES - fails} tr${PASSCODE_MAX_TRIES - fails === 1 ? 'y' : 'ies'} left.`);
                }
                return;
            }

            writeLock(null);

            if (await verifySuperAdmin())
            {
                closePasscode();
                setSuperAdmin(true);
                setActiveTab('custom');
            }
            else
            {
                closePasscode();
                errorN('Sorry: You are not authorized to perform this action. Please contact system administrator.');
            }
        }
        finally
        {
            setPassChecking(false);
        }
    }

    function openLog()
    {
        setLogRows(readCustomLog());
        setLogOpen(true);
    }

    async function loadSettings() {
        try
        {
            let obj = JSON.parse(localStorage.getItem('currentproperty'));

            if (obj === null)
            {
                errorN(`Choose a property to proceed.`);
                return;
            }

            let settingList = await get(`/api/hotels/${obj.id}/settings`)
            if (response.ok)
            {
                let recs = {};
                for (let x of settingList)
                {
                    if (GROUPS[x.type] !== undefined && recs[x.type] === undefined)
                    {
                        recs[x.type] = x;
                        fillForm(x.type, x);
                    }
                }

                // clear any group that is not set up, so no values from an earlier load are left behind
                for (let t of Object.keys(GROUPS))
                {
                    if (recs[t] === undefined)
                    {
                        initial.current[t] = undefined;
                        if (!(GROUPS[t].superAdmin === true && !superAdminRef.current))
                            forms[t].resetFields();
                    }
                }

                setRecords(recs);
                setLoaded(true);

                await loadLookups(obj.id);
            }
            else
            {
                if(response.status === 401)
                {
                    localStorage.removeItem('session');
                    window.location.reload();
                }
                else
                {
                    errorN(`Load System Settings - ${response.status}: ${response.statusText}. Please contact the administrator.`);
                }
            }
        }
        catch (err)
        {
            errorN(`Error Load System Settings: ${err}. Please contact the administrator.`);
        }
    }

    // Same sources as the Windows screen: currencies from the common lists, suppliers from parties,
    // resource types from the property's reservation items
    async function loadLookups(hotelId) {
        try
        {
            let commonList = await get(`/api/lists`)
            if (response.ok)
            {
                for (let x of commonList)
                {
                    if (x.type === 'currency' && x.data !== null)
                        setCurrencyList([...x.data].sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)));
                }
            }

            let partyList = await get(`/api/parties?type=supplier`)
            if (response.ok)
            {
                setSupplierList(partyList.filter(x => x.data !== null && x.data.supplier !== undefined && x.data.supplier !== null)
                    .map(x => ({id: x.id.toString(), name: x.data.name})));
            }

            let itemList = await get(`/api/hotels/${hotelId}/items`)
            if (response.ok)
            {
                setRoomTypeList(itemList.filter(x => x.data !== null && Array.isArray(x.data.type) && x.data.type[0] === 'reservation')
                    .map(x => ({id: x.id.toString(), name: x.data.name})));
            }
        }
        catch (err)
        {
            errorN(`Error Load Lists: ${err}. Please contact the administrator.`);
        }
    }

    // Refreshes the cached settings in localStorage after a successful save. Called only on success,
    // so on any failure the cache stays as it was.
    async function refreshCache(type, saved)
    {
        try
        {
            let obj = JSON.parse(localStorage.getItem('currentproperty'));
            let settingList = await get(`/api/hotels/${obj.id}/settings`)
            if (!response.ok)
                return;

            for (let x of settingList)
            {
                if (x.type === 'custom')
                {
                    localStorage.setItem('customsetting', JSON.stringify(x));
                }
                if (x.type === type)
                {
                    saved = x;
                }
            }

            if (GROUPS[type].cacheKey !== null)
            {
                localStorage.setItem(GROUPS[type].cacheKey, JSON.stringify(saved));
            }

            if (type === 'systemparameter' && saved.data !== null && saved.data.currency !== undefined && saved.data.currency !== null)
            {
                localStorage.setItem('currencycode', saved.data.currency.code === 'MYR' ? 'RM' : saved.data.currency.code);
            }

            // same values index.js sets from the custom record at login
            if (type === 'custom' && saved.data !== null && saved.data !== undefined)
            {
                localStorage.setItem('unitcustomname', saved.data.unitName !== null && saved.data.unitName !== undefined && saved.data.unitName !== '' ? saved.data.unitName : 'Unit');
                localStorage.setItem('fullpaymentbeforeinvoice', saved.data.fullPaymentBeforeInvoice);

                dispatch({type:'SYS_AVAILABILITYMANUALOVERRIDE',payload: saved.data.availabilityManualOverride})
                dispatch({type:'SYS_STOPSALE',payload: saved.data.stopSale})
                dispatch({type:'SYS_RATECONTROL',payload: saved.data.rateControl})
            }

            if (type === 'notice')
            {
                localStorage.setItem('folionotice', JSON.stringify(saved.data !== null ? saved.data.folio !== null ? saved.data.folio : '' : ''));
                localStorage.setItem('receiptnotice', JSON.stringify(saved.data !== null ? saved.data.receipt !== null ? saved.data.receipt : '' : ''));
                localStorage.setItem('foliofooter', JSON.stringify(saved.data !== null ? saved.data.footer !== null ? saved.data.footer : DEFAULT_FOOTER : DEFAULT_FOOTER));
            }
        }
        catch (err)
        {
        }
    }

    // text shown in the confirm dialog for one value
    function describe(f, v)
    {
        if (f.secret)
            return '********';
        if (f.kind === 'list')
            return `${(v || []).length} row(s)`;
        if (f.kind === 'strings')
            return (v || []).length === 0 ? '(none)' : v.join(', ');
        if (f.kind === 'bool')
            return v ? 'Yes' : 'No';
        if (v === undefined || v === null || v === '')
            return '(blank)';
        return v.toString();
    }

    function storeValue(f, v)
    {
        if (f.kind === 'currency')
        {
            let c = currencyList.find(x => x.code === v);
            return {code: v, name: c !== undefined ? c.name : v};
        }
        return toStored(f, v);
    }

    async function onSave(type)
    {
        if (busy.current)
            return;

        let record = records[type];

        if (record === undefined)
        {
            errorN(`${GROUPS[type].title} is not set up for this property. Please set it in the Windows application.`);
            return;
        }

        if (GROUPS[type].superAdmin === true && !superAdmin)
        {
            errorN('Sorry: You are not authorized to perform this action. Please contact system administrator.');
            return;
        }

        busy.current = true;

        try
        {
            await forms[type].validateFields();
        }
        catch (err)
        {
            busy.current = false;
            errorN(`Please correct the highlighted fields.`);
            return;
        }

        const values = forms[type].getFieldsValue(true);

        let changed = changedFields(type);

        if (changed.length === 0)
        {
            busy.current = false;
            successN('No changes to save.');
            return;
        }

        Modal.confirm({
            title: `Update ${GROUPS[type].title}?`,
            content: (
                <>
                    {changed.map(f => <div key={f.name}>{f.label}: <b>{describe(f, initial.current[type][f.name])}</b> &rarr; <b>{describe(f, values[f.name])}</b></div>)}
                </>
            ),
            okText: 'Update',
            cancelText: 'Cancel',
            onOk: async () => {
                try
                {
                    await saveNow(type, changed, values);
                }
                finally
                {
                    busy.current = false;
                }
            },
            onCancel: () => {
                busy.current = false;
            }
        });
    }

    async function saveNow(type, changed, values)
    {
        let record = records[type];

        try
        {
            if (GROUPS[type].superAdmin === true && !(await verifySuperAdmin()))
            {
                setActiveTab('time');
                setSuperAdmin(false);
                errorN('Sorry: You are not authorized to perform this action. Please contact system administrator.');
                return;
            }

            // fetch the latest copy of this group's record and apply only the edited fields to it
            let fresh = await get(`/api/settings/${record.id}`)
            if (!response.ok)
            {
                if(response.status === 401)
                {
                    localStorage.removeItem('session');
                    window.location.reload();
                }
                else
                {
                    errorN(`Update ${GROUPS[type].title} - ${response.status}: ${response.statusText}. Settings were not saved.`);
                }
                return;
            }

            if (fresh === null || fresh === undefined || fresh.id !== record.id || fresh.type !== type)
            {
                errorN(`Update ${GROUPS[type].title}: unexpected settings record returned. Settings were not saved.`);
                return;
            }

            if (fresh.data === null || fresh.data === undefined)
                fresh.data = {};

            // a list is replaced as a whole, so refuse to overwrite one that changed since this page loaded it
            let conflicts = changed.filter(f => (f.kind === 'list' || f.kind === 'strings') &&
                !sameValue(toForm(f, getPath(fresh.data, f.path)), toForm(f, getPath(record.data, f.path))));

            if (conflicts.length > 0)
            {
                errorN(`${conflicts.map(f => f.label).join(', ')} changed since this page was loaded. Please Reload and make your changes again. Settings were not saved.`);
                return;
            }

            for (let f of changed)
            {
                setPath(fresh.data, f.path, storeValue(f, values[f.name]));
            }

            // the Windows app always writes both shift entries, so fill in any missing half with its defaults
            if (type === 'time' && changed.some(f => f.path[0] === 'shift'))
            {
                for (let f of GROUPS.time.fields.filter(x => x.path[0] === 'shift'))
                {
                    if (getPath(fresh.data, f.path) === undefined || getPath(fresh.data, f.path) === null)
                        setPath(fresh.data, f.path, toStored(f, values[f.name]));
                }
            }

            // likewise channelAccess is always written with username, password and lcode together
            if (type === 'channel' && changed.some(f => f.path[0] === 'channelAccess'))
            {
                for (let f of GROUPS.channel.fields.filter(x => x.path[0] === 'channelAccess'))
                {
                    if (getPath(fresh.data, f.path) === undefined || getPath(fresh.data, f.path) === null)
                        setPath(fresh.data, f.path, storeValue(f, values[f.name]));
                }
            }

            let saved = await put(`/api/settings/${record.id}`, fresh)
            if (response.ok)
            {
                successN(`${GROUPS[type].title} updated successfully.`);

                if (type === 'custom')
                {
                    let user = '';
                    try
                    {
                        user = localStorage.getItem('username') || localStorage.getItem('name') || '';
                    }
                    catch (e)
                    {
                    }
                    let time = new Date().toISOString();
                    addCustomLog(changed.map(f => ({time: time, user: user, field: f.label, from: describe(f, initial.current[type][f.name]), to: describe(f, values[f.name])})));
                }

                let latest = saved !== null && saved !== undefined && saved.data !== undefined ? saved : fresh;
                setRecords(prev => ({...prev, [type]: latest}));
                fillForm(type, latest);

                await refreshCache(type, latest);
            }
            else
            {
                if(response.status === 401)
                {
                    localStorage.removeItem('session');
                    window.location.reload();
                }
                else
                {
                    errorN(`Update ${GROUPS[type].title} - ${response.status}: ${response.statusText}. Settings were not saved.`);
                }
            }
        }
        catch (err)
        {
            errorN(`Error Update ${GROUPS[type].title}: ${err}. Settings were not saved.`);
        }
    }

    const timeRule = {
        validator: (_, value) => {
            if (value === undefined || value === null || value.trim() === '')
                return Promise.reject('Please input time');
            return TIME_RX.test(value.trim()) ? Promise.resolve() : Promise.reject('Use HH:mm or HH:mm:ss (24-hour)');
        }
    };

    const hhmmRule = {
        validator: (_, value) => {
            if (value === undefined || value === null || value.trim() === '')
                return Promise.reject('Please input hh:mm');
            return HHMM_RX.test(value.trim()) ? Promise.resolve() : Promise.reject('Use hh:mm (00:00 - 23:59)');
        }
    };

    const emailRule = {
        validator: (_, value) => {
            if (value === undefined || value === null || value.trim() === '')
                return Promise.resolve();
            let bad = value.split(/[;,]/).map(x => x.trim()).filter(x => x !== '' && !EMAIL_RX.test(x));
            return bad.length === 0 ? Promise.resolve() : Promise.reject(`Invalid email: ${bad.join(', ')}`);
        }
    };

    // Windows: "<Type> No. is not valid. Enter numbers only." / Length is a numeric mask
    const numberRule = (label) => ({
        validator: (_, value) => {
            let v = value === undefined || value === null ? '' : value.toString().trim();
            return /^\d+$/.test(v) && v.length <= 9 ? Promise.resolve() : Promise.reject(`${label} is not valid. Enter numbers only.`);
        }
    });

    const decimalRule = (label, required) => ({
        validator: (_, value) => {
            let v = value === undefined || value === null ? '' : value.toString().trim();
            if (v === '')
                return required ? Promise.reject(`Please input ${label}`) : Promise.resolve();
            return DECIMAL_RX.test(v) ? Promise.resolve() : Promise.reject(`${label} is not valid. Enter numbers only.`);
        }
    });

    const timezoneRule = {
        validator: (_, value) => {
            let v = value === undefined || value === null ? '' : value.trim();
            if (v === '')
                return Promise.reject('Please input Time Zone');
            if (!TIMEZONE_RX.test(v) || parseInt(v.replace('-', '').split(':')[0], 10) > 14)
                return Promise.reject('Use UTC offset hh:mm, e.g. 08:00 or -05:00');
            return Promise.resolve();
        }
    };

    // Windows "Get" button: offset of this computer from UTC
    const getTimeZone = () => {
        let m = -new Date().getTimezoneOffset();
        let sign = m < 0 ? '-' : '';
        m = Math.abs(m);
        sysForm.setFieldsValue({timezone: `${sign}${Math.floor(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}:00`});
    };

    const missing = (type) => loaded && records[type] === undefined ?
        <Alert type="warning" showIcon message={`${GROUPS[type].title} is not set up for this property. Please set it in the Windows application.`} style={{marginBottom: '10px'}} />
        : null;

    const updateButton = (type, label) => (
        <Button type="danger" onClick={() => onSave(type)} loading={loading} disabled={records[type] === undefined}>
            <StepForwardOutlined /> {label !== undefined ? label : 'Update'}
        </Button>
    );

    const tabButtons = (
        <Space>
            {state.role === 'administrator' ?
                <Button onClick={onSuperAdmin} type={superAdmin ? 'primary' : 'default'}>
                    {superAdmin ? <><UnlockOutlined /> Lock Super Admin</> : <><LockOutlined /> Super Admin</>}
                </Button>
                : null
            }
            <Button onClick={onReload} loading={loading}>
                <ReloadOutlined /> Reload
            </Button>
        </Space>
    );

    if (!allowed)
    {
        return (
            <Result status="403" title="403" subTitle="Sorry, you are not authorized to access System Settings." />
        );
    }

    return (
        <>
            <div style={{ backgroundImage:`url("/header-bg7.png")`, color:'black', fontSize: '18px', paddingLeft: '15px', paddingTop: '15px',paddingBottom: '15px',marginTop: '0px'}}>
                &nbsp;&nbsp;<ControlOutlined />
                &nbsp;&nbsp;System Settings
            </div>
            <Layout>
                <Spin spinning={loading}>
                    <Content style={{backgroundColor: 'white', padding: '0px 20px 20px 20px'}}>
                        <Tabs activeKey={activeTab} onChange={setActiveTab} tabBarExtraContent={tabButtons}>

                            <TabPane tab="Date & Time" key="time" forceRender>
                                {missing('time')}
                                <Form layout="vertical" form={timeForm}>
                                    <Row>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Check In" name="start" rules={[timeRule]}>
                                                <Input style={{ width: '50%' }} placeholder="HH:mm:ss" />
                                            </Form.Item>
                                            <Form.Item label="Check Out" name="end" rules={[timeRule]}>
                                                <Input style={{ width: '50%' }} placeholder="HH:mm:ss" />
                                            </Form.Item>
                                            <Form.Item label="Check Out Buffer (hh:mm)" name="buffer" rules={[hhmmRule]}>
                                                <Input style={{ width: '50%' }} placeholder="hh:mm" />
                                            </Form.Item>
                                            <Form.Item label="Cut Out Time" name="cutOut" rules={[timeRule]}>
                                                <Input style={{ width: '50%' }} placeholder="HH:mm:ss" />
                                            </Form.Item>
                                            <Form.Item label="Housekeeping (hh:mm)" name="housekeeping" rules={[hhmmRule]}>
                                                <Input style={{ width: '50%' }} placeholder="hh:mm" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Early Check In (hours)" name="earlyCheckin" extra="Hours before the check in time that early check in is allowed." rules={[{required: true, message: 'Please choose hours'}]}>
                                                <Select style={{ width: '50%' }}>
                                                    {EARLY_CHECKIN.map(v => <Option key={v} value={v}>{v}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item label="Late Check Out (hours)" name="lateCheckout" extra="Maximum hours offered for late check out." rules={[{required: true, message: 'Please choose hours'}]}>
                                                <Select style={{ width: '50%' }}>
                                                    {LATE_CHECKOUT.map(v => <Option key={v} value={v}>{v}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Divider orientation="left" plain>Shift</Divider>
                                            <Form.Item label="First Shift Begin" name="shiftStartTime" rules={[timeRule]}>
                                                <Input style={{ width: '50%' }} placeholder="HH:mm:ss" />
                                            </Form.Item>
                                            <Form.Item name="shiftStartPeriod" rules={[{required: true, message: 'Please choose day'}]}>
                                                <Select style={{ width: '50%' }}>
                                                    {PERIODS.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item label="Last Shift End" name="shiftEndTime" rules={[timeRule]}>
                                                <Input style={{ width: '50%' }} placeholder="HH:mm:ss" />
                                            </Form.Item>
                                            <Form.Item name="shiftEndPeriod" rules={[{required: true, message: 'Please choose day'}]}>
                                                <Select style={{ width: '50%' }}>
                                                    {PERIODS.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {updateButton('time')}
                                </Form>
                            </TabPane>

                            <TabPane tab="Notice" key="notice" forceRender>
                                {missing('notice')}
                                <Form layout="vertical" form={noticeForm}>
                                    <Row>
                                        <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Folio Notice" name="folio">
                                                <TextArea rows={5} placeholder="Folio Notice" />
                                            </Form.Item>
                                            <Form.Item label="Invoice Notice" name="invoice">
                                                <TextArea rows={5} placeholder="Invoice Notice" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Payment Slip Notice" name="receipt">
                                                <TextArea rows={5} placeholder="Payment Slip Notice" />
                                            </Form.Item>
                                            <Form.Item label="Folio & Payment Slip Footer" name="footer">
                                                <TextArea rows={3} placeholder="Folio & Payment Slip Footer" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {updateButton('notice')}
                                </Form>
                            </TabPane>

                            <TabPane tab="Email" key="email" forceRender>
                                {missing('email')}
                                <Form layout="vertical" form={emailForm}>
                                    <Row>
                                        <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Notification" name="notification" rules={[emailRule]}>
                                                <Input placeholder="Email" />
                                            </Form.Item>
                                            <Form.Item label="Complaint" name="complaint" rules={[emailRule]}>
                                                <Input placeholder="Email" />
                                            </Form.Item>
                                            <Form.Item label="Reservation" name="reservation" rules={[emailRule]}>
                                                <Input placeholder="Email" />
                                            </Form.Item>
                                            <Form.Item label="Enquiry" name="enquiry" rules={[emailRule]}>
                                                <Input placeholder="Email" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Notification CC" name="notificationCC" rules={[emailRule]}>
                                                <Input placeholder="Email (separate multiple with ;)" />
                                            </Form.Item>
                                            <Form.Item label="Complaint CC" name="complaintCC" rules={[emailRule]}>
                                                <Input placeholder="Email (separate multiple with ;)" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {updateButton('email')}
                                </Form>
                            </TabPane>

                            <TabPane tab="Running No." key="runningno" forceRender>
                                <Row style={{padding: '0px 20px', fontWeight: 'bold'}}>
                                    <Col span={4}></Col>
                                    <Col span={5}>Prefix</Col>
                                    <Col span={4}>Length</Col>
                                    <Col span={5}>No.</Col>
                                    <Col span={6}></Col>
                                </Row>
                                {RUNNING_NO.map(r => (
                                    <Form key={r.type} name={r.type} form={forms[r.type]} layout="inline" style={{padding: '8px 20px'}}>
                                        <Row style={{width: '100%'}}>
                                            <Col span={4} style={{paddingTop: '5px'}}>{r.title}</Col>
                                            <Col span={5}>
                                                <Form.Item name="prefix" style={{width: '90%'}}>
                                                    <Input placeholder="Prefix" disabled={records[r.type] === undefined} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item name="length" style={{width: '90%'}} rules={[numberRule('Length')]}>
                                                    <Input placeholder="Length" disabled={records[r.type] === undefined} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={5}>
                                                <Form.Item name="lastNo" style={{width: '90%'}} rules={[numberRule(`${r.title} No.`)]}>
                                                    <Input placeholder="No." disabled={records[r.type] === undefined} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                {records[r.type] === undefined && loaded ? <span style={{color: '#faad14'}}>Not set up for this property</span> : updateButton(r.type)}
                                            </Col>
                                        </Row>
                                    </Form>
                                ))}
                            </TabPane>

                            <TabPane tab="System Parameter" key="systemparameter" forceRender>
                                {missing('systemparameter')}
                                <Form layout="vertical" form={sysForm}>
                                    <Row>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Currency" name="currency" rules={[{required: true, message: 'Choose a currency to proceed.'}]}>
                                                <Select placeholder="Currency" showSearch optionFilterProp="children">
                                                    {currencyList.map(v => <Option key={v.code} value={v.code}>{v.name}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item label="Time Zone (UTC offset)" name="timezone" rules={[timezoneRule]}>
                                                <Input placeholder="08:00:00" style={{width: '220px'}} addonAfter={<span style={{cursor: 'pointer'}} onClick={getTimeZone}><FieldTimeOutlined /> Get</span>} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Deposit" name="defaultDeposit" rules={[decimalRule('Deposit', true)]}>
                                                <Input style={{ width: '50%' }} placeholder="0" />
                                            </Form.Item>
                                            <Form.Item label="Rounding" name="roundingAmount" extra="Blank saves 0.05." rules={[decimalRule('Rounding', false)]}>
                                                <Input style={{ width: '50%' }} placeholder="0.05" />
                                            </Form.Item>
                                            <Form.Item name="skipRounding" valuePropName="checked">
                                                <Checkbox>Skip Rounding For Booking & Folio</Checkbox>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {updateButton('systemparameter')}
                                </Form>
                            </TabPane>

                            <TabPane tab="Fees" key="fee" forceRender>
                                {missing('fee')}
                                <Form layout="vertical" form={feeForm}>
                                    <Row>
                                        <Col xs={24} sm={24} md={24} lg={16} xl={12} style={{padding: '0px 20px'}}>
                                            <Form.Item name="fees">
                                                <FeeList disabled={records.fee === undefined} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {updateButton('fee')}
                                </Form>
                            </TabPane>

                            <TabPane tab="Guest Parameter" key="guestParameter" forceRender>
                                {missing('guestParameter')}
                                <Form layout="vertical" form={guestForm}>
                                    <Row>
                                        <Col xs={24} sm={24} md={24} lg={12} xl={12} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Newspapers" name="newspaper">
                                                <Select mode="multiple" allowClear placeholder="Newspapers">
                                                    {NEWSPAPERS.map(v => <Option key={v} value={v}>{v}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {updateButton('guestParameter')}
                                </Form>
                            </TabPane>

                            <TabPane tab="Channel" key="channel" forceRender>
                                {missing('channel')}
                                <Form layout="vertical" form={channelForm}>
                                    <Row>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Form.Item label="Channel Manager" name="channelName">
                                                <Select style={{ width: '50%' }}>
                                                    {CHANNEL_NAMES.map(v => <Option key={v} value={v}>{v === '' ? '(none)' : v}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item label="Channel Username" name="username">
                                                <Input autoComplete="off" />
                                            </Form.Item>
                                            <Form.Item label="Channel Password" name="password">
                                                <Input.Password autoComplete="new-password" />
                                            </Form.Item>
                                            <Form.Item label="Property Code" name="lcode">
                                                <Input autoComplete="off" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Divider orientation="left" plain>OTA</Divider>
                                            <Form.Item name="otaChannels">
                                                <OtaChannelList suppliers={supplierList} disabled={records.channel === undefined} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Divider orientation="left" plain>Room Codes</Divider>
                                            <Form.Item name="otaRooms">
                                                <RoomCodeList roomTypes={roomTypeList} disabled={records.channel === undefined} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {updateButton('channel')}
                                </Form>
                            </TabPane>

                            <TabPane tab={<><WifiOutlined /> Devices</>} key="device" forceRender>
                                {missing('device')}
                                <Form layout="vertical" form={deviceForm}
                                    onValuesChange={(changed) => {
                                        // Windows clears the lock type and code when Door Lock is unticked
                                        if (changed.doorLock === false)
                                            deviceForm.setFieldsValue({lockType: '', lockCode: ''});
                                    }}>
                                    <Alert type="info" showIcon style={{margin: '0px 20px 10px 20px'}} message="For hardware integration, please install Ratri Hardware services." />
                                    <Row>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Divider plain orientation="left">Door Lock</Divider>
                                            <Form.Item name="doorLock" valuePropName="checked">
                                                <Checkbox>Enable Door Lock</Checkbox>
                                            </Form.Item>
                                            <Form.Item noStyle shouldUpdate={(a, b) => a.doorLock !== b.doorLock}>
                                                {({getFieldValue}) => {
                                                    const on = getFieldValue('doorLock') === true;
                                                    const required = (label) => ({validator(_, v) {
                                                        return on && (v || '').trim() === '' ? Promise.reject(new Error(`${label} is required when Door Lock is on.`)) : Promise.resolve();
                                                    }});
                                                    // The lock vendor list comes from the Windows hardware DLL, so offer the saved type and allow typing.
                                                    const saved = initial.current.device !== undefined ? initial.current.device.lockType : '';
                                                    const options = Array.from(new Set([saved].filter(v => v !== undefined && v !== null && v !== ''))).map(v => ({value: v}));
                                                    return (
                                                        <>
                                                            <Form.Item label="Lock Type" name="lockType" rules={[required('Lock Type')]}>
                                                                <AutoComplete options={options} disabled={!on} placeholder="Lock type" style={{width: '220px'}} filterOption={false} />
                                                            </Form.Item>
                                                            <Form.Item label="Lock Code" name="lockCode" rules={[required('Lock Code')]}>
                                                                <Input disabled={!on} placeholder="Lock code" style={{width: '220px'}} />
                                                            </Form.Item>
                                                        </>
                                                    );
                                                }}
                                            </Form.Item>

                                            <Divider plain orientation="left">MyKad Reader</Divider>
                                            <Form.Item name="mykad" valuePropName="checked">
                                                <Checkbox>Enable MyKad Reader</Checkbox>
                                            </Form.Item>

                                            <Divider plain orientation="left">Connection</Divider>
                                            <Form.Item extra="Changed in the Windows app (super admin).">
                                                <Radio.Group disabled value={records.device !== undefined && records.device.data !== null && records.device.data !== undefined && records.device.data.services === true ? 'services' : 'builtin'}>
                                                    <Radio value="builtin">Built-In</Radio>
                                                    <Radio value="services">Services</Radio>
                                                </Radio.Group>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                            <Divider plain orientation="left">WiFi</Divider>
                                            <Form.Item name="wifi" valuePropName="checked" extra="Creates a WiFi login for the unit at check-in and removes it at check-out, through the local service on this PC.">
                                                <Checkbox>Enable WiFi Guest Accounts</Checkbox>
                                            </Form.Item>
                                            <Form.Item label="Router IP" name="routerIP" dependencies={['wifi']}
                                                rules={[({getFieldValue}) => ({validator(_, v) {
                                                    return getFieldValue('wifi') === true && (v || '').trim() === '' ? Promise.reject(new Error('Router IP is required when WiFi is on.')) : Promise.resolve();
                                                }})]}>
                                                <Input placeholder="192.168.0.1" style={{width: '220px'}} />
                                            </Form.Item>
                                            <Form.Item label="Guest WiFi Name (SSID)" name="guestSSID" extra='Printed on receipts as "Please login to ...". Blank prints "Wifi".'>
                                                <Input placeholder="Wifi" style={{width: '220px'}} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {updateButton('device')}
                                </Form>
                            </TabPane>

                            {superAdmin ?
                                <TabPane tab={<><UnlockOutlined /> Custom</>} key="custom" forceRender>
                                    {missing('custom')}
                                    <Form layout="vertical" form={customForm}>
                                        <Row>
                                            <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                                <Form.Item label="Custom Unit Name" name="unitName" extra='Blank saves "Unit".' rules={[{max: 30, message: 'Maximum 30 characters'}]}>
                                                    <Input style={{ width: '60%' }} placeholder="Unit" />
                                                </Form.Item>
                                                {GROUPS.custom.fields.filter(f => f.kind === 'bool').slice(0, 5).map(f =>
                                                    <Form.Item key={f.name} name={f.name} valuePropName="checked" style={{marginBottom: '4px'}}>
                                                        <Checkbox>{f.label}</Checkbox>
                                                    </Form.Item>
                                                )}
                                            </Col>
                                            <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                                {GROUPS.custom.fields.filter(f => f.kind === 'bool').slice(5, 11).map(f =>
                                                    <Form.Item key={f.name} name={f.name} valuePropName="checked" style={{marginBottom: '4px'}}>
                                                        <Checkbox>{f.label}</Checkbox>
                                                    </Form.Item>
                                                )}
                                            </Col>
                                            <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{padding: '0px 20px'}}>
                                                {GROUPS.custom.fields.filter(f => f.kind === 'bool').slice(11).map(f =>
                                                    <Form.Item key={f.name} name={f.name} valuePropName="checked" style={{marginBottom: '4px'}}>
                                                        <Checkbox>{f.label}</Checkbox>
                                                    </Form.Item>
                                                )}
                                            </Col>
                                        </Row>
                                        <br />
                                        <Space>
                                            {updateButton('custom')}
                                            <Button onClick={openLog}>View log</Button>
                                        </Space>
                                    </Form>
                                </TabPane>
                                : null
                            }

                        </Tabs>
                    </Content>
                </Spin>
            </Layout>

            <Modal
                title="Super Administrator Access"
                visible={passOpen}
                okText="Unlock"
                onOk={submitPasscode}
                onCancel={closePasscode}
                confirmLoading={passChecking}
                maskClosable={false}
                destroyOnClose
            >
                <p>Enter the Super Admin passcode. Custom stays unlocked until you lock it, reload or leave this page.</p>
                <Input.Password
                    id="superadminPasscode"
                    autoComplete="off"
                    value={passValue}
                    onChange={e => setPassValue(e.target.value)}
                    onPressEnter={submitPasscode}
                    disabled={passChecking}
                    autoFocus
                />
                {passError !== '' ? <div style={{color: '#cf1322', marginTop: '8px'}}>{passError}</div> : null}
            </Modal>

            <Modal
                title="Custom settings change log (this browser only)"
                visible={logOpen}
                footer={<Button onClick={() => setLogOpen(false)}>Close</Button>}
                onCancel={() => setLogOpen(false)}
                width={800}
            >
                <Table size="small" pagination={{pageSize: 10}} dataSource={logRows.map((r, i) => ({...r, key: i}))}
                    columns={[
                        {title: 'Time', dataIndex: 'time', render: t => new Date(t).toLocaleString()},
                        {title: 'User', dataIndex: 'user'},
                        {title: 'Field', dataIndex: 'field'},
                        {title: 'From', dataIndex: 'from'},
                        {title: 'To', dataIndex: 'to'},
                    ]} />
            </Modal>
        </>
    );
}

export default SystemSettings
